import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ProvideScrapEyeMetadata,
  useScrapEyeMetadata,
} from "contexts/scrapEyeMetadata";
import { useNotifyStatus } from "contexts/status";
import { isFetchBaseQueryError } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMaterials } from "hooks/useMaterials";
import { useMemo, useState } from "react";
import {
  EventLabellingModelRead,
  EventLabellingModelStatus,
  FlagRules,
  InferenceEnvironment,
  ReviewReason,
  useGetTargetedReviewReasonsInUseQuery,
  useUpdateEventLabellingModelMutation,
} from "store/api/generatedApi";
import {
  formatReviewReason,
  TARGETED_REVIEW_REASON_LABEL_KEYS,
} from "components/manualReview/utils/reviewReasons";
import {
  buildEventLabellingModelUpdate,
  FlagRulesWarning,
  flagRulesWarnings,
  INFERENCE_ENVIRONMENT_OPTIONS,
  parseAuthorityInput,
  parseThresholdInput,
  proposedFlagRules,
  sameFlagRules,
  STATUS_OPTIONS,
  unlabellableMaterials,
} from "./modelDashboardHelpers";
import { FlagRulesEditor } from "./FlagRulesEditor";
import { ModelStateHistory } from "./ModelStateHistory";
import { RecallReadout } from "./RecallReadout";

const FLAG_RULES_WARNING_KEYS: Record<FlagRulesWarning, string> = {
  authorityFlagsNothing: "modelAuthorityWithoutFlagRules",
  thresholdsWithoutLikelyError: "thresholdsNeedLikelyError",
  likelyErrorWithoutThresholds: "likelyErrorNeedsThreshold",
};

type ModelDetailProps = Readonly<{
  model: EventLabellingModelRead | null;
}>;

export const ModelDetail = ({ model }: ModelDetailProps) => {
  const { t } = useTenantTranslation();
  if (model === null) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 4,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("selectAModelToEdit")}
        </Typography>
      </Box>
    );
  }
  // Keyed remount so form state resets cleanly when selection changes.
  return (
    <ProvideScrapEyeMetadata>
      <ModelEditForm key={model.id} model={model} />
    </ProvideScrapEyeMetadata>
  );
};

const numberInput = (value: number | null): string => value?.toString() ?? "";

const ModelEditForm = ({ model }: { model: EventLabellingModelRead }) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const notifyStatus = useNotifyStatus();
  const [updateModel, { isLoading: saving }] =
    useUpdateEventLabellingModelMutation();
  const { data: reasonsInUse } = useGetTargetedReviewReasonsInUseQuery({
    tenantName,
    labellingName: model.labelling_name,
  });

  const [status, setStatus] = useState<EventLabellingModelStatus>(model.status);
  const [inferenceEnvironment, setInferenceEnvironment] =
    useState<InferenceEnvironment>(model.inference_environment);
  const [authorityInput, setAuthorityInput] = useState<string>(
    model.authority?.toString() ?? ""
  );
  const rules = model.flag_rules ?? null;
  const defaultRules =
    useScrapEyeMetadata().label_review_config.warnings.default_flag_rules;
  const initialRules = rules ?? defaultRules;
  const [materialThresholdInput, setMaterialThresholdInput] = useState<string>(
    () => numberInput(initialRules.material_confidence_threshold)
  );
  const [eventThresholdInput, setEventThresholdInput] = useState<string>(() =>
    numberInput(initialRules.event_confidence_threshold)
  );
  const [areaThresholdInput, setAreaThresholdInput] = useState<string>(() =>
    numberInput(initialRules.area_confidence_threshold)
  );
  const [reasons, setReasons] = useState<ReviewReason[]>(initialRules.reasons);
  const [quantileInput, setQuantileInput] = useState<string>(() =>
    numberInput(initialRules.mass_outlier_quantile)
  );
  const [confusableMaterials, setConfusableMaterials] = useState<string[]>(
    initialRules.confusable_materials
  );
  const [neverPlannedMaterials, setNeverPlannedMaterials] = useState<string[]>(
    initialRules.never_planned_materials
  );

  const parsedAuthority = parseAuthorityInput(authorityInput);
  const authorityInvalid =
    authorityInput.trim() !== "" &&
    (parsedAuthority === null || parsedAuthority < 0);

  const parsedMaterialThreshold = parseThresholdInput(materialThresholdInput);
  const parsedEventThreshold = parseThresholdInput(eventThresholdInput);
  const parsedAreaThreshold = parseThresholdInput(areaThresholdInput);

  const materialThresholdInvalid =
    materialThresholdInput.trim() !== "" &&
    (parsedMaterialThreshold === null ||
      parsedMaterialThreshold < 0 ||
      parsedMaterialThreshold > 1);
  const eventThresholdInvalid =
    eventThresholdInput.trim() !== "" &&
    (parsedEventThreshold === null || parsedEventThreshold < 0);
  const areaThresholdInvalid =
    areaThresholdInput.trim() !== "" &&
    (parsedAreaThreshold === null ||
      parsedAreaThreshold < 0 ||
      parsedAreaThreshold > 1);
  const parsedQuantile = parseThresholdInput(quantileInput);
  const quantileInvalid =
    quantileInput.trim() !== "" &&
    (parsedQuantile === null || parsedQuantile <= 0 || parsedQuantile >= 0.5);
  const { materials } = useMaterials();
  const labellingNameByMaterial = useMemo(
    () =>
      materials &&
      new Map(
        materials.flatMap((material) =>
          material.labelling_name === null
            ? []
            : [[material.name, material.labelling_name] as const]
        )
      ),
    [materials]
  );
  const anyMaterialUnlabellable =
    unlabellableMaterials({
      materials: [...confusableMaterials, ...neverPlannedMaterials],
      labellingNameByMaterial,
    }).length > 0;
  const anyRuleInvalid =
    materialThresholdInvalid ||
    eventThresholdInvalid ||
    areaThresholdInvalid ||
    quantileInvalid ||
    anyMaterialUnlabellable;

  const draftRules: FlagRules = {
    reasons,
    event_confidence_threshold: parsedEventThreshold,
    material_confidence_threshold: parsedMaterialThreshold,
    area_confidence_threshold: parsedAreaThreshold,
    mass_outlier_quantile: parsedQuantile,
    confusable_materials: confusableMaterials,
    never_planned_materials: neverPlannedMaterials,
  };
  // The cached site defaults can refresh after the form has shown them. Rules the
  // operator has not touched follow the refresh.
  const [shownDefaultRules, setShownDefaultRules] = useState(defaultRules);
  if (!sameFlagRules(shownDefaultRules, defaultRules)) {
    setShownDefaultRules(defaultRules);
    if (rules === null && sameFlagRules(draftRules, shownDefaultRules)) {
      setMaterialThresholdInput(
        numberInput(defaultRules.material_confidence_threshold)
      );
      setEventThresholdInput(
        numberInput(defaultRules.event_confidence_threshold)
      );
      setAreaThresholdInput(
        numberInput(defaultRules.area_confidence_threshold)
      );
      setReasons(defaultRules.reasons);
      setQuantileInput(numberInput(defaultRules.mass_outlier_quantile));
      setConfusableMaterials(defaultRules.confusable_materials);
      setNeverPlannedMaterials(defaultRules.never_planned_materials);
    }
  }
  const proposedRules = proposedFlagRules({ saved: rules, draft: draftRules });
  const warnings = flagRulesWarnings({
    status,
    authority: parsedAuthority,
    rules: proposedRules,
  });

  const dirty =
    status !== model.status ||
    inferenceEnvironment !== model.inference_environment ||
    parsedAuthority !== (model.authority ?? null) ||
    !sameFlagRules(rules, proposedRules);

  const handleSave = async () => {
    if (authorityInvalid || anyRuleInvalid || !dirty) {
      return;
    }
    const update = buildEventLabellingModelUpdate(model, {
      ...model,
      status,
      inference_environment: inferenceEnvironment,
      authority: parsedAuthority,
      flag_rules: proposedRules,
    });
    try {
      await updateModel({
        tenantName,
        modelId: model.id,
        eventLabellingModelUpdate: update,
      }).unwrap();
    } catch (err) {
      const suffix = isFetchBaseQueryError(err)
        ? ` (${String(err.status)})`
        : "";
      notifyStatus({
        type: "error",
        text: `${t("couldNotUpdateModel")}${suffix}`,
      });
    }
  };

  const handleRevert = () => {
    setStatus(model.status);
    setInferenceEnvironment(model.inference_environment);
    setAuthorityInput(model.authority?.toString() ?? "");
    setMaterialThresholdInput(
      rules?.material_confidence_threshold?.toString() ?? ""
    );
    setEventThresholdInput(rules?.event_confidence_threshold?.toString() ?? "");
    setAreaThresholdInput(rules?.area_confidence_threshold?.toString() ?? "");
    setReasons(rules?.reasons ?? []);
    setQuantileInput(rules?.mass_outlier_quantile?.toString() ?? "");
    setConfusableMaterials(rules?.confusable_materials ?? []);
    setNeverPlannedMaterials(rules?.never_planned_materials ?? []);
  };

  return (
    <Stack
      spacing={3}
      sx={{ p: 3, height: "100%", overflow: "auto", boxSizing: "border-box" }}
    >
      <Typography variant="h6">{model.labelling_name}</Typography>

      <Divider />

      <Stack spacing={2} sx={{ maxWidth: 480 }}>
        <TextField
          label={t("mlflowRunId")}
          value={model.mlflow_run_id}
          disabled
          size="small"
          fullWidth
          slotProps={{
            input: { sx: { fontFamily: "monospace" } },
            inputLabel: { shrink: true },
          }}
        />
        <TextField
          label={t("modelStatus")}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as EventLabellingModelStatus);
          }}
          select
          size="small"
          fullWidth
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {t(s)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t("inferenceEnvironment")}
          value={inferenceEnvironment}
          onChange={(e) => {
            setInferenceEnvironment(e.target.value as InferenceEnvironment);
          }}
          select
          size="small"
          fullWidth
        >
          {INFERENCE_ENVIRONMENT_OPTIONS.map((env) => (
            <MenuItem key={env} value={env}>
              {t(env)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t("authority")}
          value={authorityInput}
          onChange={(e) => {
            setAuthorityInput(e.target.value);
          }}
          type="number"
          size="small"
          fullWidth
          error={authorityInvalid}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { step: 1, min: 0 },
          }}
          helperText={
            authorityInvalid
              ? t("invalidAuthorityValue")
              : t("authorityHelperText")
          }
        />
        <Typography variant="caption" color="text.secondary">
          {t("modelStateEditsRecorded")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("likelyErrorThresholdsHelp")}
        </Typography>
        {(reasonsInUse?.reasons_in_use ?? []).map((reason) => {
          const reasonLabelKey = TARGETED_REVIEW_REASON_LABEL_KEYS[reason];
          return (
            <Alert key={reason} severity="info" variant="outlined">
              {t("targetedReviewReasonInUse", {
                reason: reasonLabelKey
                  ? t(reasonLabelKey)
                  : formatReviewReason(reason),
              })}
            </Alert>
          );
        })}
        <TextField
          label={t("materialConfidenceThreshold")}
          value={materialThresholdInput}
          onChange={(e) => {
            setMaterialThresholdInput(e.target.value);
          }}
          type="number"
          size="small"
          fullWidth
          error={materialThresholdInvalid}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { step: "any", min: 0 },
          }}
          helperText={
            materialThresholdInvalid ? t("invalidThresholdValue") : ""
          }
        />
        <TextField
          label={t("eventConfidenceThreshold")}
          value={eventThresholdInput}
          onChange={(e) => {
            setEventThresholdInput(e.target.value);
          }}
          type="number"
          size="small"
          fullWidth
          error={eventThresholdInvalid}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { step: "any", min: 0 },
          }}
          helperText={eventThresholdInvalid ? t("invalidThresholdValue") : ""}
        />
        <TextField
          label={t("areaConfidenceThreshold")}
          value={areaThresholdInput}
          onChange={(e) => {
            setAreaThresholdInput(e.target.value);
          }}
          type="number"
          size="small"
          fullWidth
          error={areaThresholdInvalid}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { step: "any", min: 0 },
          }}
          helperText={areaThresholdInvalid ? t("invalidThresholdValue") : ""}
        />
        <FlagRulesEditor
          reasons={reasons}
          onReasonsChange={setReasons}
          quantileInput={quantileInput}
          onQuantileInputChange={setQuantileInput}
          quantileInvalid={quantileInvalid}
          confusableMaterials={confusableMaterials}
          onConfusableMaterialsChange={setConfusableMaterials}
          neverPlannedMaterials={neverPlannedMaterials}
          onNeverPlannedMaterialsChange={setNeverPlannedMaterials}
          labellingNameByMaterial={labellingNameByMaterial}
        />
        {rules === null && proposedRules !== null && (
          <Alert severity="info" variant="outlined">
            {t("flagRulesPrefilledFromDefaults")}
          </Alert>
        )}
        {warnings.map((warning) => (
          <Alert key={warning} severity="warning" variant="outlined">
            {t(FLAG_RULES_WARNING_KEYS[warning])}
          </Alert>
        ))}
        <RecallReadout
          tenantName={tenantName}
          labellingName={model.labelling_name}
          materialThreshold={parsedMaterialThreshold}
          eventThreshold={parsedEventThreshold}
          areaThreshold={parsedAreaThreshold}
        />
      </Stack>

      <ModelStateHistory modelId={model.id} />

      <Stack direction="row" spacing={1} sx={{ maxWidth: 480 }}>
        <Button
          color="secondary"
          onClick={handleRevert}
          disabled={!dirty || saving}
        >
          {t("revert")}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <LoadingButton
          variant="contained"
          onClick={() => void handleSave()}
          loading={saving}
          disabled={!dirty || authorityInvalid || anyRuleInvalid}
        >
          {t("save")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
